import React, { useState, useEffect } from 'react';
import { useIsLoggedIn, useDisplayName } from '../state';
import { getUserData } from '../services/userService';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const Contact = () => {
  const isLoggedIn = useIsLoggedIn();
  const displayName = useDisplayName();
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    if (!isLoggedIn) return;
    const fetchEmail = async () => {
      try {
        const data = await getUserData('email');
        setUserEmail(data.email || '');
      } catch (err) {
        console.error('Failed to fetch user email:', err);
      }
    };
    fetchEmail();
  }, [isLoggedIn]);

  const isAutofilled = isLoggedIn && userEmail;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null); // 'sending' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const senderName = isAutofilled && displayName ? displayName : name;
  const senderEmail = isAutofilled ? userEmail : email;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    try {
      const res = await fetch(`${apiBaseUrl}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: senderName.trim(),
          email: senderEmail.trim(),
          subject: subject.trim(),
          message: message.trim(),
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 mt-12 pb-16">
      <article className="bg-white text-black rounded-xl shadow-lg p-6 md:p-8">
        <h1 className="text-3xl font-extrabold mb-2">Contact Us</h1>
        <p className="text-gray-600 mb-6">
          Have a question, issue, or suggestion? Send us a message and we'll get back to you as soon as possible.
        </p>

        {status === 'success' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
            <p className="text-green-800 font-semibold text-lg mb-1">Message sent!</p>
            <p className="text-green-700 text-sm">We'll get back to you shortly.</p>
            <button
              type="button"
              onClick={() => setStatus(null)}
              className="mt-4 text-sm underline text-green-700 hover:text-green-900"
            >
              Send another message
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name & Email — hidden when logged in */}
            {!isAutofilled && (
              <>
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#68C7CC] focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#68C7CC] focus:border-transparent"
                    placeholder="you@example.com"
                  />
                </div>
              </>
            )}

            {/* Subject */}
            <div>
              <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                id="contact-subject"
                type="text"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#68C7CC] focus:border-transparent"
                placeholder="What is this about?"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                id="contact-message"
                required
                rows={6}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#68C7CC] focus:border-transparent resize-y"
                placeholder="Tell us more..."
              />
            </div>

            {status === 'error' && (
              <p className="text-red-600 text-sm">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="w-full bg-[#68C7CC] hover:bg-[#55b5ba] text-white font-semibold py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'sending' ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}

        {/* Company Details */}
        <hr className="my-8 border-gray-200" />
        <div className="text-sm text-gray-500 space-y-1">
          <h2 className="text-base font-semibold text-gray-700 mb-2">Company information</h2>
          <p><span className="font-medium text-gray-600">Registered name:</span> Tipl Ltd</p>
          <p><span className="font-medium text-gray-600">Company number:</span> 16065318</p>
          <p><span className="font-medium text-gray-600">Registered address:</span> 82A James Carter Road, Mildenhall, IP28 7DE, UK</p>
          <p><span className="font-medium text-gray-600">Email:</span>{' '}
            <a href="mailto:support@tipl.co" className="underline hover:text-gray-700">support@tipl.co</a>
          </p>
        </div>
      </article>
    </div>
  );
};

export default Contact;
